import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { apiFetch } from '../api';

const API_URL = process.env.REACT_APP_API_URL || '';

// Toll bills rarely match our computed amount to the penny, so amount search
// matches anything within the larger of ±15% or ±$2 of the entered value.
function amountInRange(target, value) {
  if (value == null) return false;
  const tol = Math.max(Math.abs(target) * 0.15, 2);
  return Math.abs(value - target) <= tol;
}

export default function TripsLog({ subAccountId, trackers }) {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterTrackerId, setFilterTrackerId] = useState('all');
  const [endingTripId, setEndingTripId] = useState(null);

  // Reconciliation filters (client-side, applied to the loaded trips list).
  const [searchRenter, setSearchRenter] = useState('');
  const [searchAmount, setSearchAmount] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Toll-segment expansion state. Map<trip_id, { loading, segments, error }>
  const [tollState, setTollState] = useState({});
  // Track which trips are currently expanded
  const [expandedTripIds, setExpandedTripIds] = useState(new Set());
  // Trips for which the operator has clicked Re-analyze
  const [reanalyzingTripId, setReanalyzingTripId] = useState(null);

  const loadTrips = useCallback(() => {
    setLoading(true);
    const tid = filterTrackerId === 'all' ? '' : '&tracker_id=' + filterTrackerId;
    apiFetch('/api/trips?sub_account_id=' + subAccountId + tid)
      .then(data => setTrips(data))
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, [filterTrackerId, subAccountId]);

  useEffect(() => { loadTrips(); }, [loadTrips]);

  const handleManualEnd = async (trip) => {
    if (!window.confirm(`Manually end trip for ${trip.tracker_name} (${trip.contact_name})? This will close the trip with current mileage.`)) return;
    setEndingTripId(trip.id);
    try {
      await apiFetch('/api/trips/manual-end', {
        method: 'POST',
        body: JSON.stringify({ sub_account_id: subAccountId, trip_id: trip.id }),
      });
      loadTrips();
    } catch (err) {
      alert('Failed to end trip: ' + err.message);
    } finally {
      setEndingTripId(null);
    }
  };

  // Toggle toll-segment row. Lazy-loads segments the first time a trip is
  // expanded; subsequent toggles reuse the cached data.
  const toggleTollExpansion = async (trip) => {
    const next = new Set(expandedTripIds);
    if (next.has(trip.id)) {
      next.delete(trip.id);
      setExpandedTripIds(next);
      return;
    }
    next.add(trip.id);
    setExpandedTripIds(next);

    if (tollState[trip.id]?.segments) return; // already loaded

    setTollState(s => ({ ...s, [trip.id]: { loading: true, segments: null, error: null } }));
    try {
      const segments = await apiFetch(`/api/trips/${trip.id}/tolls?sub_account_id=${subAccountId}`);
      setTollState(s => ({ ...s, [trip.id]: { loading: false, segments, error: null } }));
    } catch (err) {
      setTollState(s => ({ ...s, [trip.id]: { loading: false, segments: null, error: err.message } }));
    }
  };

  // Manually re-run toll analysis for a trip. Used when toll_analysis_at is
  // null (analysis failed at trip end) or when the operator wants to re-check.
  const handleReanalyze = async (trip) => {
    setReanalyzingTripId(trip.id);
    try {
      const result = await apiFetch(`/api/trips/${trip.id}/analyze-tolls`, {
        method: 'POST',
        body: JSON.stringify({ sub_account_id: subAccountId }),
      });
      if (!result.ok) {
        alert(`Toll analysis didn't complete: ${result.reason || 'unknown'}`);
      }
      // Force-refresh segments cache, then refresh the trips list so
      // toll totals/counts update in the row.
      setTollState(s => {
        const copy = { ...s };
        delete copy[trip.id];
        return copy;
      });
      setExpandedTripIds(prev => {
        const next = new Set(prev);
        next.add(trip.id);
        return next;
      });
      loadTrips();
      try {
        const segments = await apiFetch(`/api/trips/${trip.id}/tolls?sub_account_id=${subAccountId}`);
        setTollState(s => ({ ...s, [trip.id]: { loading: false, segments, error: null } }));
      } catch (err) {
        setTollState(s => ({ ...s, [trip.id]: { loading: false, segments: null, error: err.message } }));
      }
    } catch (err) {
      alert('Re-analysis failed: ' + err.message);
    } finally {
      setReanalyzingTripId(null);
    }
  };

  function formatDate(ts) {
    if (!ts) return 'N/A';
    return new Date(ts * 1000).toLocaleDateString('en-US');
  }

  function formatTime(ts) {
    if (!ts) return 'N/A';
    return new Date(ts * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  // For the End column: if the trip ended on a different calendar day than it
  // started, prefix the day delta (e.g. "+1d 04:13 PM") so multi-day rentals
  // don't look like they ran backwards in time.
  function formatEnd(startTs, endTs) {
    if (!endTs) return 'N/A';
    const t = formatTime(endTs);
    if (!startTs) return t;
    const sd = new Date(startTs * 1000);
    const ed = new Date(endTs * 1000);
    const startDay = new Date(sd.getFullYear(), sd.getMonth(), sd.getDate());
    const endDay = new Date(ed.getFullYear(), ed.getMonth(), ed.getDate());
    const dayDiff = Math.round((endDay - startDay) / 86400000);
    return dayDiff > 0 ? `+${dayDiff}d ${t}` : t;
  }

  function formatDuration(start, end) {
    if (!start || !end) return 'N/A';
    const mins = Math.round((end - start) / 60);
    if (mins < 60) return mins + 'm';
    return Math.floor(mins / 60) + 'h ' + (mins % 60) + 'm';
  }

  // Money formatter. Returns null-safe string; '—' for null/undefined.
  function money(v, currency) {
    if (v == null) return null;
    const cur = currency || 'USD';
    return `${cur === 'USD' ? '$' : cur + ' '}${Number(v).toFixed(2)}`;
  }

  function handleExport() {
    let url = API_URL + '/api/trips/export?sub_account_id=' + subAccountId;
    if (filterTrackerId !== 'all') url += '&tracker_id=' + filterTrackerId;
    window.open(url, '_blank');
  }

  function clearFilters() {
    setSearchRenter('');
    setSearchAmount('');
    setDateFrom('');
    setDateTo('');
  }

  // Apply the reconciliation filters client-side. The trailer dropdown is
  // already applied server-side via loadTrips, so we only layer renter / amount
  // / date on top here.
  const filteredTrips = useMemo(() => {
    const renterQ = searchRenter.trim().toLowerCase();
    const amountQ = searchAmount.trim() === '' ? null : parseFloat(searchAmount);
    // Date inputs are yyyy-mm-dd (local). Convert to epoch-second bounds.
    const fromEpoch = dateFrom ? new Date(dateFrom + 'T00:00:00').getTime() / 1000 : null;
    const toEpoch = dateTo ? new Date(dateTo + 'T23:59:59').getTime() / 1000 : null;

    return trips.filter(trip => {
      // Renter name contains-match
      if (renterQ && !(trip.contact_name || '').toLowerCase().includes(renterQ)) {
        return false;
      }
      // Amount: match if EITHER the tag total or the pay-by-mail/cash total is
      // near the entered value. Operators may be holding a tag statement or a
      // pay-by-mail bill, so we check both.
      if (amountQ != null && !Number.isNaN(amountQ)) {
        const tag = trip.toll_total_tag_cost;
        const cash = trip.toll_total_cash_cost;
        const inTag = amountInRange(amountQ, tag != null ? Number(tag) : null);
        const inCash = amountInRange(amountQ, cash != null ? Number(cash) : null);
        if (!inTag && !inCash) return false;
      }
      // Date range against the trip's start date
      if (fromEpoch != null && (trip.started_at || 0) < fromEpoch) return false;
      if (toEpoch != null && (trip.started_at || 0) > toEpoch) return false;
      return true;
    });
  }, [trips, searchRenter, searchAmount, dateFrom, dateTo]);

  const anyFilterActive = searchRenter || searchAmount || dateFrom || dateTo;

  // Stats reflect the full loaded set (not the filtered view) so the headline
  // numbers stay stable while an operator searches.
  const totalTrips = trips.filter(t => t.status === 'completed').length;
  const totalMiles = trips.reduce((sum, t) => sum + (t.trip_miles || 0), 0);
  const activeTrips = trips.filter(t => t.status === 'active').length;
  const tollTrips = trips.filter(t => (t.toll_segment_count || 0) > 0).length;

  const inputStyle = {
    padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px',
  };

  return (
    <div className="alerts-panel">
      <div className="alerts-header">
        <div>
          <h2>Rental Trips</h2>
          <p>Automatically logged when rentals start and end via RentFlow workflows.</p>
        </div>
        <button className="btn-ghost" onClick={handleExport} style={{ fontSize: '13px' }}>Export CSV</button>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '8px' }}>
        <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '16px' }}>
          <div style={{ fontSize: '24px', fontWeight: '700' }}>{totalTrips}</div>
          <div style={{ fontSize: '12px', color: '#64748b' }}>Completed trips</div>
        </div>
        <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '16px' }}>
          <div style={{ fontSize: '24px', fontWeight: '700' }}>{totalMiles.toFixed(0)}</div>
          <div style={{ fontSize: '12px', color: '#64748b' }}>Total miles rented</div>
        </div>
        <div style={{
          background: tollTrips > 0 ? '#fef3c7' : 'white',
          border: '1px solid ' + (tollTrips > 0 ? '#d97706' : '#e2e8f0'),
          borderRadius: '8px', padding: '16px'
        }}>
          <div style={{ fontSize: '24px', fontWeight: '700', color: tollTrips > 0 ? '#b45309' : '#0f172a' }}>{tollTrips}</div>
          <div style={{ fontSize: '12px', color: tollTrips > 0 ? '#b45309' : '#64748b' }}>Trips with tolls</div>
        </div>
        <div style={{
          background: activeTrips > 0 ? '#dcfce7' : 'white',
          border: '1px solid ' + (activeTrips > 0 ? '#16a34a' : '#e2e8f0'),
          borderRadius: '8px', padding: '16px'
        }}>
          <div style={{ fontSize: '24px', fontWeight: '700', color: activeTrips > 0 ? '#16a34a' : '#0f172a' }}>{activeTrips}</div>
          <div style={{ fontSize: '12px', color: activeTrips > 0 ? '#16a34a' : '#64748b' }}>Active rentals</div>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap' }}>
        <label style={{ fontSize: '12px', fontWeight: '600', color: '#475569' }}>Trailer:</label>
        <select
          value={filterTrackerId}
          onChange={e => setFilterTrackerId(e.target.value)}
          style={inputStyle}>
          <option value="all">All trailers</option>
          {trackers.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>

        <input
          type="text"
          value={searchRenter}
          onChange={e => setSearchRenter(e.target.value)}
          placeholder="Renter name"
          style={{ ...inputStyle, width: '150px' }}
        />

        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span style={{ fontSize: '13px', color: '#94a3b8' }}>$</span>
          <input
            type="number"
            value={searchAmount}
            onChange={e => setSearchAmount(e.target.value)}
            placeholder="Toll amount"
            step="0.01"
            min="0"
            style={{ ...inputStyle, width: '110px' }}
            title="Finds trips with a toll total close to this amount (±15% or ±$2)"
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <label style={{ fontSize: '12px', color: '#475569' }}>From</label>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={inputStyle} />
          <label style={{ fontSize: '12px', color: '#475569' }}>to</label>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={inputStyle} />
        </div>

        {anyFilterActive && (
          <button
            onClick={clearFilters}
            style={{
              padding: '6px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '600',
              border: '1px solid #cbd5e1', background: '#f8fafc', color: '#475569', cursor: 'pointer',
            }}>
            Clear filters
          </button>
        )}
      </div>

      {anyFilterActive && !loading && (
        <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '8px' }}>
          Showing {filteredTrips.length} of {trips.length} trips
        </div>
      )}

      {loading ? (
        <div className="loading-screen"><div className="spinner" /></div>
      ) : trips.length === 0 ? (
        <div className="empty-state">
          <h3>No trips recorded yet</h3>
          <p>Set up RentFlow workflows to automatically log rental start and end events.</p>
        </div>
      ) : filteredTrips.length === 0 ? (
        <div className="empty-state">
          <h3>No trips match your filters</h3>
          <p>Try widening the date range or amount, or clear the filters.</p>
        </div>
      ) : (
        <div style={{ background: 'white', borderRadius: '8px', border: '1px solid #e2e8f0', overflow: 'auto' }}>
          <table className="history-table" style={{ width: '100%', minWidth: '750px' }}>
            <thead>
              <tr>
                <th>Date</th>
                <th>Trailer</th>
                <th>Renter</th>
                <th>Start</th>
                <th>End</th>
                <th>Duration</th>
                <th>Miles</th>
                <th>Tolls</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredTrips.map(trip => {
                const tollCount = trip.toll_segment_count || 0;
                const isCompleted = trip.status === 'completed';
                const isExpanded = expandedTripIds.has(trip.id);
                const tollData = tollState[trip.id];
                // toll_analysis_at is null = analysis hasn't run (or failed)
                const analyzed = trip.toll_analysis_at != null;
                // Tag-rate total is the headline billable figure when present.
                const tagTotal = trip.toll_total_tag_cost != null ? Number(trip.toll_total_tag_cost) : null;
                const tollCurrency = trip.toll_currency || 'USD';
                const tagLabel = money(tagTotal, tollCurrency);

                return (
                  <React.Fragment key={trip.id}>
                    <tr>
                      <td style={{ whiteSpace: 'nowrap' }}>{formatDate(trip.started_at)}</td>
                      <td><strong>{trip.tracker_name || 'N/A'}</strong></td>
                      <td>{trip.contact_name || 'N/A'}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{formatTime(trip.started_at)}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{formatEnd(trip.started_at, trip.ended_at)}</td>
                      <td>{formatDuration(trip.started_at, trip.ended_at)}</td>
                      <td>{trip.trip_miles ? trip.trip_miles.toFixed(1) + ' mi' : 'N/A'}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {!isCompleted ? (
                          <span style={{ color: '#94a3b8', fontSize: '12px' }}>—</span>
                        ) : tollCount > 0 ? (
                          <button
                            onClick={() => toggleTollExpansion(trip)}
                            style={{
                              background: '#fef3c7',
                              color: '#b45309',
                              border: '1px solid #fcd34d',
                              padding: '2px 8px',
                              borderRadius: '12px',
                              fontSize: '11px',
                              fontWeight: '600',
                              cursor: 'pointer',
                            }}
                            title="Click to see toll details and amounts"
                          >
                            {/* Show the billable tag total if we have it; otherwise fall
                                back to the legacy segment count (Overpass-era rows). */}
                            {tagLabel ? tagLabel : `${tollCount} toll${tollCount === 1 ? '' : 's'}`}
                            {' '}{isExpanded ? '▴' : '▾'}
                          </button>
                        ) : analyzed ? (
                          <span style={{ color: '#94a3b8', fontSize: '12px' }}>None</span>
                        ) : (
                          <button
                            onClick={() => handleReanalyze(trip)}
                            disabled={reanalyzingTripId === trip.id}
                            style={{
                              background: '#f1f5f9',
                              color: '#475569',
                              border: '1px solid #cbd5e1',
                              padding: '2px 8px',
                              borderRadius: '12px',
                              fontSize: '11px',
                              fontWeight: '600',
                              cursor: 'pointer',
                            }}
                            title="Toll analysis hasn't run for this trip yet. Click to run it now."
                          >
                            {reanalyzingTripId === trip.id ? 'Analyzing…' : 'Analyze'}
                          </button>
                        )}
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{
                            background: trip.status === 'active' ? '#dcfce7' : '#f1f5f9',
                            color: trip.status === 'active' ? '#16a34a' : '#64748b',
                            padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600',
                            whiteSpace: 'nowrap',
                          }}>
                            {trip.status === 'active' ? 'Active' : 'Completed'}
                          </span>
                          {trip.status === 'active' && (
                            <button
                              onClick={() => handleManualEnd(trip)}
                              disabled={endingTripId === trip.id}
                              style={{
                                padding: '2px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: '600',
                                border: '1px solid #fecaca', background: '#fef2f2', color: '#dc2626',
                                cursor: 'pointer', whiteSpace: 'nowrap',
                              }}>
                              {endingTripId === trip.id ? 'Ending...' : 'End Trip'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={9} style={{ background: '#fffbeb', padding: '12px 16px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: '12px', fontWeight: '700', color: '#92400e', marginBottom: '6px' }}>
                                Toll roads used during this rental
                              </div>
                              {tollData?.loading && (
                                <div style={{ fontSize: '12px', color: '#64748b' }}>Loading toll details…</div>
                              )}
                              {tollData?.error && (
                                <div style={{ fontSize: '12px', color: '#dc2626' }}>Couldn't load tolls: {tollData.error}</div>
                              )}
                              {tollData?.segments && tollData.segments.length === 0 && (
                                <div style={{ fontSize: '12px', color: '#64748b' }}>No tolls recorded.</div>
                              )}
                              {tollData?.segments && tollData.segments.length > 0 && (
                                <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                                  <thead>
                                    <tr style={{ color: '#92400e', textAlign: 'left' }}>
                                      <th style={{ padding: '4px 8px 4px 0', fontWeight: '600' }}>Toll road / plaza</th>
                                      <th style={{ padding: '4px 8px', fontWeight: '600' }}>Time</th>
                                      <th style={{ padding: '4px 8px', fontWeight: '600', textAlign: 'right' }}>Tag</th>
                                      <th style={{ padding: '4px 8px', fontWeight: '600', textAlign: 'right' }}>Pay-by-mail</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {tollData.segments.map(seg => {
                                      const tag = money(seg.tag_cost, seg.currency);
                                      const lp = money(seg.license_plate_cost, seg.currency);
                                      const label = seg.plaza_name
                                        ? `${seg.road_name} — ${seg.plaza_name}`
                                        : seg.road_name;
                                      return (
                                        <tr key={seg.id}>
                                          <td style={{ padding: '4px 8px 4px 0', fontWeight: '600' }}>{label}</td>
                                          <td style={{ padding: '4px 8px', color: '#475569' }}>
                                            {seg.entered_at ? formatTime(seg.entered_at) : '—'}
                                          </td>
                                          <td style={{ padding: '4px 8px', color: '#475569', textAlign: 'right' }}>{tag || '—'}</td>
                                          <td style={{ padding: '4px 8px', color: '#475569', textAlign: 'right' }}>{lp || '—'}</td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                  <tfoot>
                                    <tr style={{ borderTop: '1px solid #fcd34d', fontWeight: '700', color: '#92400e' }}>
                                      <td style={{ padding: '6px 8px 0 0' }}>Trip total</td>
                                      <td style={{ padding: '6px 8px 0' }} />
                                      <td style={{ padding: '6px 8px 0', textAlign: 'right' }}>
                                        {money(trip.toll_total_tag_cost, tollCurrency) || '—'}
                                      </td>
                                      <td style={{ padding: '6px 8px 0', textAlign: 'right' }}>
                                        {/* Pay-by-mail trip total: sum the plaza license-plate costs,
                                            since trips store tag/cash totals but not an LP total. */}
                                        {(() => {
                                          const lpSum = tollData.segments.reduce(
                                            (s, seg) => s + (seg.license_plate_cost != null ? Number(seg.license_plate_cost) : 0),
                                            0
                                          );
                                          const anyLp = tollData.segments.some(seg => seg.license_plate_cost != null);
                                          return anyLp ? money(lpSum, tollCurrency) : '—';
                                        })()}
                                      </td>
                                    </tr>
                                  </tfoot>
                                </table>
                              )}
                              <div style={{ fontSize: '11px', color: '#92400e', marginTop: '8px', fontStyle: 'italic' }}>
                                Toll amounts via TollGuru. <strong>Tag</strong> is the transponder rate (TxTag, EZ-Tag);
                                {' '}<strong>pay-by-mail</strong> is the higher rate billed to a license plate when no
                                transponder is used. Bill the renter whichever rate matches the charge on your toll account.
                              </div>
                            </div>
                            <button
                              onClick={() => handleReanalyze(trip)}
                              disabled={reanalyzingTripId === trip.id}
                              style={{
                                fontSize: '11px', fontWeight: '600',
                                padding: '4px 10px', borderRadius: '6px',
                                background: 'white', color: '#92400e',
                                border: '1px solid #fcd34d', cursor: 'pointer',
                                whiteSpace: 'nowrap',
                              }}
                              title="Re-run toll analysis for this trip"
                            >
                              {reanalyzingTripId === trip.id ? 'Re-analyzing…' : 'Re-analyze'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
